// Formatting + variance-severity helpers shared by the silo/counts screens.
// Thresholds below are a UI-only judgment call (BUILD_SPEC.md §8.2/§8.6 say
// "variance highlighted" but do not pin exact bands) — kept in one place so every
// screen agrees on what counts as "fine" vs "needs a look" vs "investigate".
export type VarianceSeverity = 'ok' | 'warn' | 'high';

const WARN_PCT = 5;
const HIGH_PCT = 15;

/** Classifies a variance for badge coloring. Prefers percent when a ledger
 * baseline exists (non-null `variancePct`); falls back to a small absolute-kg
 * band when the ledger baseline was zero (percent is undefined against zero). */
export function varianceSeverity(varianceKg: string, variancePct: string | null): VarianceSeverity {
  if (variancePct !== null) {
    const pct = Math.abs(Number(variancePct));
    if (pct >= HIGH_PCT) return 'high';
    if (pct >= WARN_PCT) return 'warn';
    return 'ok';
  }
  const kg = Math.abs(Number(varianceKg));
  if (kg >= 500) return 'high';
  if (kg >= 50) return 'warn';
  return 'ok';
}

// Aligned to the theme's semantic palette (theme.ts: brandGreen=posted/success,
// brandOrange=warning/variance, brandRed=error/blocked — branded-neutral
// ruling, 2026-08-24). `amber` is reserved for draft/pending elsewhere in the
// app, so a variance warning uses brandOrange, not amber.
export function severityColor(severity: VarianceSeverity): string {
  switch (severity) {
    case 'high':
      return 'brandRed';
    case 'warn':
      return 'brandOrange';
    default:
      return 'brandGreen';
  }
}

/** `kg` decimal strings can be large (silo capacities run into the tens of
 * tonnes) — display with thousands separators for legibility (§11: legible,
 * tablet-friendly), 3dp to match the ledger's own precision (packages/core
 * quantitySchema). */
export function formatKg(value: string | number): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('en-ZA', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`;
}

export function formatTonnes(value: string | number): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return `${(n / 1000).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} t`;
}

export function formatPct(value: string | number | null): string {
  if (value === null) return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
}

/** Signed kg with an explicit `+` for positive deltas, since "the ledger was
 * short" vs "the ledger was long" is exactly the fact an operator needs at a
 * glance in a reconciliation preview. */
export function formatSignedKg(value: string | number): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toLocaleString('en-ZA', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`;
}

/** Signed quantity with no fixed unit suffix — stock-count lines can cover any
 * product base UOM (KG/EACH/L), unlike silo readings which are always mass. */
export function formatSignedQty(value: string | number): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toLocaleString('en-ZA', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
}

/** Unsigned counterpart of `formatSignedQty` — same `en-ZA` comma-decimal
 * formatting, no `+`/`-` sign. `expectedQty`/`countedQty` were previously
 * rendered as raw API strings (period-decimal, e.g. "123.4") sitting right next
 * to a `formatSignedQty` delta ("123,4" comma-decimal) in the same row — mixed
 * separators in one glance. Every quantity in a stock-count preview/table must
 * go through one of these two formatters so they always agree. */
export function formatQty(value: string | number | null): string {
  if (value === null) return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-ZA', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
